import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { ecrireContrat, getContrat, getReferentiel, reinitialiserContrat }
  from '@/shared/api/bridge';

/**
 * Le contrat de raccordement : ce qu'on annonce à CET agrégateur, et d'où ça vient.
 *
 * POURQUOI CE BLOC EXISTE
 *
 * Tout ce qui décrit comment on parle à un agrégateur était codé en dur, et en un seul
 * exemplaire — celui de Survoltage, appliqué à tout le monde. Raccorder un partenaire
 * qui dit « BATTERY », qui veut de l'énergie plutôt que de la puissance, ou qui
 * échantillonne au quart d'heure, demandait d'ouvrir un éditeur de code.
 *
 * LE CHAMP « SOURCE », ET POURQUOI C'EST UN MENU ET JAMAIS UNE SAISIE
 *
 *     nom annoncé   ce que l'agrégateur attend        « activePower »
 *     source        ce que NOUS mesurons vraiment     « puissance_kw »
 *
 * Les deux ne coïncidaient jusqu'ici que par coïncidence : la déclaration annonçait
 * `activePower` et l'envoi poussait `puissance_kw`, sans que rien ne les relie. Le menu
 * ne propose que les grandeurs réellement persistées pour ce type d'actif. Un champ
 * libre ferait de ce formulaire le moyen le plus court de promettre à un tiers une
 * donnée que personne n'enregistre — et le serveur la refuserait de toute façon.
 *
 * CE QUE L'ÉCRAN DIT QUAND IL NE PEUT RIEN PROPOSER
 *
 * Un Ballon, une ECS, un PV n'ont aujourd'hui AUCUNE grandeur persistée. Le bloc le dit
 * en toutes lettres plutôt que d'afficher un menu vide : « rien ne peut lui être annoncé
 * tant que sa télémétrie n'est pas enregistrée » se corrige, un menu vide se subit.
 */

const LIBELLE_TYPE = {
  Batterie: 'Batterie', PAC: 'Pompe à chaleur', Ballon: 'Ballon',
  ECS: 'Eau chaude sanitaire', PV: 'Photovoltaïque',
};

export default function ContratRaccordement({ agregateur, surEnregistrement }) {
  const [contrat, setContrat] = useState(null);
  const [referentiel, setReferentiel] = useState(null);
  const [typeOuvert, setTypeOuvert] = useState('Batterie');
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let vivant = true;
    setErreur(''); setMessage('');
    Promise.all([getContrat(agregateur), getReferentiel()])
      .then(([c, r]) => { if (vivant) { setContrat(c); setReferentiel(r); } })
      .catch((exc) => vivant
        && setErreur(exc?.response?.data?.detail || 'Contrat illisible'));
    return () => { vivant = false; };
  }, [agregateur]);

  const grandeursPossibles = useMemo(
    () => referentiel?.grandeurs?.[typeOuvert] || [], [referentiel, typeOuvert]);

  const bloc = contrat?.types?.[typeOuvert];

  const modifier = (transformation) => {
    setContrat((precedent) => ({
      ...precedent,
      types: { ...precedent.types, [typeOuvert]: transformation(precedent.types[typeOuvert]) },
    }));
    setMessage('');
  };

  const modifierGrandeur = (rang, champ, valeur) => modifier((b) => ({
    ...b,
    metering: b.metering.map((g, i) => (i === rang ? { ...g, [champ]: valeur } : g)),
  }));

  const ajouterGrandeur = () => modifier((b) => ({
    ...b,
    metering: [...(b.metering || []), {
      // La première grandeur non encore utilisée : proposer un doublon ferait échouer
      // l'enregistrement sur une règle que l'écran connaît déjà.
      name: '', unit: 'W', duration: referentiel?.periode_mesure_s || 300,
      type: 'AVERAGE', source: grandeursPossibles[0] || '', facteur: 1,
      signe: 'injection',
    }],
  }));

  const retirerGrandeur = (rang) => modifier((b) => ({
    ...b, metering: b.metering.filter((_, i) => i !== rang),
  }));

  const basculerCompteur = (champ) => modifier((b) => {
    const presents = b.compteurs_preuve || [];
    return presents.some((c) => c.champ === champ)
      ? { ...b, compteurs_preuve: presents.filter((c) => c.champ !== champ) }
      : { ...b, compteurs_preuve: [...presents, { champ, sens: `relevé de ${champ}` }] };
  });

  const enregistrer = async () => {
    setEnCours(true); setErreur(''); setMessage('');
    try {
      const rendu = await ecrireContrat(agregateur, contrat.types);
      setContrat(rendu);
      setMessage(
        `Contrat enregistré. ${rendu.declarations_mises_a_jour} déclaration(s) `
        + 'active(s) réalignée(s) : ce qu\'on annonce et ce qu\'on pousse restent la '
        + 'même chose.');
      surEnregistrement?.();
    } catch (exc) {
      setErreur(exc?.response?.data?.detail || 'Enregistrement refusé');
    } finally { setEnCours(false); }
  };

  const reinitialiser = async () => {
    setEnCours(true); setErreur(''); setMessage('');
    try {
      const rendu = await reinitialiserContrat(agregateur);
      setContrat(await getContrat(agregateur));
      setMessage(`Contrat remis aux valeurs proposées, `
        + `${rendu.declarations_mises_a_jour} déclaration(s) réalignée(s).`);
      surEnregistrement?.();
    } catch (exc) {
      setErreur(exc?.response?.data?.detail || 'Réinitialisation impossible');
    } finally { setEnCours(false); }
  };

  if (!contrat || !referentiel) {
    return (
      <Card data-testid="bridge-contrat">
        <CardContent className="pt-5 text-sm text-neutral-500">
          {erreur || 'Lecture du contrat de raccordement…'}
        </CardContent>
      </Card>
    );
  }

  const sansMesure = grandeursPossibles.length === 0;

  return (
    <Card data-testid="bridge-contrat">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          Contrat de raccordement
          {contrat.par_defaut
            ? <Badge variant="outline" className="font-normal">valeurs proposées</Badge>
            : <Badge variant="outline" className="font-normal">
                modifié {contrat.modifie_par ? `par ${contrat.modifie_par}` : ''}
              </Badge>}
        </CardTitle>
        <p className="text-[13px] text-neutral-500 leading-relaxed max-w-3xl">
          Ce qu&apos;on annonce à {agregateur} : son vocabulaire de type, les grandeurs
          qu&apos;on s&apos;engage à pousser, et ce qui sert de preuve d&apos;effacement.
          La colonne <strong>source</strong> dit d&apos;où chaque grandeur est
          réellement lue — c&apos;est elle qui empêche d&apos;annoncer une donnée que
          personne n&apos;enregistre.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {(referentiel.types_declarables || []).map((t) => (
            <Button key={t} size="sm"
                    variant={typeOuvert === t ? 'default' : 'outline'}
                    onClick={() => setTypeOuvert(t)}
                    data-testid={`bridge-contrat-type-${t}`}>
              {LIBELLE_TYPE[t] || t}
              {(referentiel.grandeurs?.[t] || []).length === 0 && (
                <span className="ml-1.5 opacity-60">·</span>
              )}
            </Button>
          ))}
        </div>

        {erreur && (
          <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-[13px]"
               data-testid="bridge-contrat-erreur">
            {erreur}
          </div>
        )}
        {message && (
          <div className="rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-[13px]"
               data-testid="bridge-contrat-message">
            {message}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-neutral-700">
            Nom du type chez {agregateur}
          </label>
          <Input value={bloc?.type_declare || ''} className="max-w-xs"
                 data-testid="bridge-contrat-type-declare"
                 onChange={(e) => modifier((b) => ({ ...b, type_declare: e.target.value }))} />
          <p className="text-[11px] text-neutral-500">
            Notre « {LIBELLE_TYPE[typeOuvert] || typeOuvert} » s&apos;appelle ainsi chez
            eux. C&apos;est l&apos;agrégateur qui a le dernier mot sur son propre
            vocabulaire.
          </p>
        </div>

        {/* ---- Les grandeurs annoncées ------------------------------------------ */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-[13px] font-medium text-neutral-800">
              Grandeurs annoncées et poussées
            </h4>
            <Button size="sm" variant="outline" onClick={ajouterGrandeur}
                    disabled={sansMesure} data-testid="bridge-contrat-ajouter">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
          </div>

          {sansMesure && (
            <div className="rounded-md bg-amber-50 text-amber-900 px-3 py-2.5 text-[13px]
                            flex gap-2" data-testid="bridge-contrat-sans-mesure">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Aucune grandeur n&apos;est enregistrée pour un actif
                {' '}{LIBELLE_TYPE[typeOuvert] || typeOuvert}. Rien ne peut lui être
                annoncé tant que sa télémétrie n&apos;est pas persistée
                (<code>core/metriques.MESURES</code>) — l&apos;annoncer quand même
                promettrait une donnée que personne ne mesure.
              </span>
            </div>
          )}

          {(bloc?.metering || []).map((grandeur, rang) => (
            <div key={rang}
                 className="rounded-md border border-neutral-200 p-3 grid gap-2
                            md:grid-cols-12 items-end"
                 data-testid={`bridge-contrat-grandeur-${rang}`}>
              <div className="md:col-span-3 space-y-1">
                <label className="text-[11px] text-neutral-500">Nom annoncé</label>
                <Input value={grandeur.name || ''} placeholder="activePower"
                       onChange={(e) => modifierGrandeur(rang, 'name', e.target.value)} />
              </div>
              <div className="md:col-span-3 space-y-1">
                <label className="text-[11px] text-neutral-500">
                  Source (ce qu&apos;on mesure)
                </label>
                <select
                  className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm
                             bg-white"
                  value={grandeur.source || ''}
                  data-testid={`bridge-contrat-source-${rang}`}
                  onChange={(e) => modifierGrandeur(rang, 'source', e.target.value)}>
                  <option value="">— choisir —</option>
                  {grandeursPossibles.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1 space-y-1">
                <label className="text-[11px] text-neutral-500">Unité</label>
                <Input value={grandeur.unit || ''}
                       onChange={(e) => modifierGrandeur(rang, 'unit', e.target.value)} />
              </div>
              <div className="md:col-span-1 space-y-1">
                <label className="text-[11px] text-neutral-500">×</label>
                <Input value={grandeur.facteur ?? 1} type="number"
                       onChange={(e) => modifierGrandeur(
                         rang, 'facteur', Number(e.target.value))} />
              </div>
              <div className="md:col-span-1 space-y-1">
                <label className="text-[11px] text-neutral-500">Pas (s)</label>
                <Input value={grandeur.duration ?? 300} type="number"
                       onChange={(e) => modifierGrandeur(
                         rang, 'duration', Number(e.target.value))} />
              </div>
              <div className="md:col-span-1 space-y-1">
                <label className="text-[11px] text-neutral-500">Agrégat</label>
                <select className="w-full h-9 rounded-md border border-neutral-200 px-1
                                   text-xs bg-white"
                        value={grandeur.type || 'AVERAGE'}
                        onChange={(e) => modifierGrandeur(rang, 'type', e.target.value)}>
                  {(referentiel.agregations || []).map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1 space-y-1">
                <label className="text-[11px] text-neutral-500" title={
                  'injection : valeur absolue, le sens part dans le drapeau. '
                  + 'brut : la valeur telle quelle, signe compris.'}>Signe</label>
                <select className="w-full h-9 rounded-md border border-neutral-200 px-1
                                   text-xs bg-white"
                        value={grandeur.signe || 'injection'}
                        onChange={(e) => modifierGrandeur(rang, 'signe', e.target.value)}>
                  {(referentiel.signes || []).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1 flex justify-end">
                <Button size="sm" variant="ghost" onClick={() => retirerGrandeur(rang)}
                        title="Retirer cette grandeur">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            </div>
          ))}

          {!sansMesure && (bloc?.metering || []).length === 0 && (
            <p className="text-[13px] text-neutral-500">
              Aucune grandeur annoncée : cet agrégateur ne recevra aucune mesure pour ce
              type d&apos;actif.
            </p>
          )}
        </div>

        {/* ---- Les compteurs de preuve ------------------------------------------ */}
        {!sansMesure && (
          <div className="space-y-2">
            <h4 className="text-[13px] font-medium text-neutral-800">
              Compteurs qui servent de preuve d&apos;effacement
            </h4>
            <p className="text-[11px] text-neutral-500 max-w-3xl">
              L&apos;énergie d&apos;une attestation est la <strong>différence</strong> de
              deux relevés de compteur sur la fenêtre. Le premier coché est le compteur
              principal, celui qui donne le chiffre.
            </p>
            <div className="flex flex-wrap gap-2">
              {grandeursPossibles.map((champ) => {
                const rang = (bloc?.compteurs_preuve || [])
                  .findIndex((c) => c.champ === champ);
                return (
                  <button key={champ} type="button" onClick={() => basculerCompteur(champ)}
                          data-testid={`bridge-contrat-compteur-${champ}`}
                          className={`text-[11px] px-2 py-1 rounded-md border ${rang >= 0
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-neutral-50 text-neutral-500 border-neutral-200'}`}>
                    {rang === 0 ? '① ' : rang > 0 ? `${rang + 1} ` : ''}{champ}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" onClick={enregistrer} disabled={enCours}
                  data-testid="bridge-contrat-enregistrer">
            <Save className="w-4 h-4 mr-1.5" /> Enregistrer le contrat
          </Button>
          <Button size="sm" variant="outline" onClick={reinitialiser} disabled={enCours}>
            <RotateCcw className="w-4 h-4 mr-1.5" /> Valeurs proposées
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
